'use client';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cx } from 'class-variance-authority';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface Props {
    title?: React.ReactNode | null | string | number;
    /** @deprecated */
    small?: true | boolean;
    noSpacer?: true | boolean;
    collapsible?: true | boolean;
    defaultExpanded?: true | boolean;
    children?: React.ReactNode;
    fieldKeys?: string[];
    className?: string;
}

export const FormDivider = ({
    title,
    noSpacer,
    collapsible,
    children,
    fieldKeys,
    className,
    defaultExpanded
}: Props) => {
    const formContext = useFormContext();
    const errorKeys = Object.keys(formContext?.formState.errors || {});
    const submitCount = formContext?.formState.submitCount || 0;
    const [collapsed, setCollapsed] = useState(collapsible && !defaultExpanded);

    const hasInvalidFields = useMemo(
        () => errorKeys.some((key) => fieldKeys?.some((fKey) => key.startsWith(fKey))),
        [errorKeys, fieldKeys]
    );

    useEffect(() => {
        if (hasInvalidFields) setCollapsed(false);
    }, [submitCount, hasInvalidFields]);

    /*
     * Title left, chevron beside it.
     *
     * This was a two-column grid with the chevron in the first cell and the
     * title right-aligned in the second, so a heading sat at the right edge
     * with its own control stranded half a form-width away — and a long one
     * ("Termine, die nicht berechnet werden") wrapped to two right-aligned
     * lines above a full-width rule. Every field label in the same form is
     * left-aligned, so the eye had to cross the form and come back for each
     * section.
     *
     * The whole row is the control when the section folds, rather than a
     * click-handler on a div plus a small button inside it: that pair was
     * unreachable by keyboard and announced nothing about being expanded.
     */
    const heading = (
        <>
            <span className="tw-min-w-0 tw-text-lg">{title}</span>
            {collapsible && (
                <FontAwesomeIcon
                    icon={collapsed ? faChevronDown : faChevronUp}
                    className="tw-flex-none tw-text-sm tw-text-accents-7"
                />
            )}
        </>
    );

    return (
        <div className={cx(!noSpacer && 'tw-mt-6', 'first:tw-mt-2', className)}>
            {title &&
                (collapsible ? (
                    <button
                        type="button"
                        aria-expanded={!collapsed}
                        // A section forced open by an error underneath it is not
                        // a section you may close again — the error would go
                        // back out of sight.
                        disabled={hasInvalidFields}
                        className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-left disabled:tw-cursor-default"
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {heading}
                    </button>
                ) : (
                    <div className="tw-flex tw-w-full tw-items-center tw-gap-2">{heading}</div>
                ))}
            <div className="tw-my-2 tw-w-full tw-border-0 tw-border-b tw-border-solid tw-border-accents-3" />
            <div className={cx('tw-overflow-hidden tw-transition-all', collapsed ? 'tw-max-h-0' : 'tw-max-h-max')}>
                {children}
            </div>
        </div>
    );
};

export default FormDivider;
